import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import Box from '../Box';
import Text, { setDefaultTextColor } from '../Text';
import theme, { listStyle, pipedText, spacedPipedText } from '../theme';
import { useThemeDefaults } from '../utils';
import hoistNonReactStatics from '../hoist';
var TextStyleContext = /*#__PURE__*/React.createContext(theme.copyTextStyle);

var providesTextStyle = function providesTextStyle(Component) {
  return withTheme(function (props) {
    return /*#__PURE__*/React.createElement(TextStyleContext.Provider, {
      value: props.textStyle || props.theme.copyTextStyle
    }, /*#__PURE__*/React.createElement(Component, props));
  });
};

var consumesTextStyle = function consumesTextStyle(Component) {
  var hoc = function hoc(props) {
    return /*#__PURE__*/React.createElement(TextStyleContext.Consumer, null, function (textStyle) {
      return /*#__PURE__*/React.createElement(Component, _extends({
        textStyle: textStyle
      }, props));
    });
  };

  hoistNonReactStatics(hoc, Component);
  hoc.displayName = "consumesTextStyle(".concat(Component.displayName, ")");
  return hoc;
};

var listBase = {
  listStylePosition: 'outside',
  overflow: 'initial'
};
var BaseList = styled(Box).withConfig({
  displayName: "List__BaseList",
  componentId: "sc-hbprcd-0"
})(listBase, listStyle);
/**
 * Space out the list items except the last, which should have a negative
 * bottom margin to account for line height
 */

var SpacedList = function SpacedList(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(BaseList, props, React.Children.map(children, function (child, i) {
    return i === children.length - 1 ? /*#__PURE__*/React.isValidElement(child) && /*#__PURE__*/React.cloneElement(child, _objectSpread({
      sb: 'spacer0'
    }, child.props)) : child;
  }));
};

export var UL = providesTextStyle(useThemeDefaults(function (props) {
  return {
    as: 'ul',
    listStyle: 'unordered',
    ml: props.listStyle === 'noBullet' ? 0 : props.theme.listMargins.ul,
    color: setDefaultTextColor(props)
  };
}, SpacedList));
UL.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), listStyle.propTypes);
export var OL = providesTextStyle(useThemeDefaults(function (props) {
  return {
    as: 'ol',
    listStyle: 'ordered',
    ml: props.theme.listMargins.ol,
    color: setDefaultTextColor(props)
  };
}, SpacedList));
OL.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), listStyle.propTypes);
export var LI = consumesTextStyle(useThemeDefaults(function () {
  return {
    as: 'li',
    overflow: 'initial',
    spaceBelow: 'list'
  };
}, Text));
LI.propTypes = _objectSpread({}, Text.propTypes);
export var Inline = consumesTextStyle(useThemeDefaults(function (props) {
  return {
    as: 'ul',
    color: setDefaultTextColor(props)
  };
}, styled(Box).withConfig({
  displayName: "List__Inline",
  componentId: "sc-hbprcd-1"
})(["", " ", "{display:inline;:not(:first-child){padding-left:20px;}}"], listBase, LI)));
Inline.propTypes = _objectSpread({}, Box.propTypes);
var spacedBetween = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  flexWrap: "wrap"
};
export var Piped = providesTextStyle(useThemeDefaults(function (props) {
  return {
    as: "ul",
    color: setDefaultTextColor(props)
  };
}, styled(Box).withConfig({
  displayName: "List__Piped",
  componentId: "sc-hbprcd-2"
})(["", " ", " ", "{", " ", "}"], listBase, function (props) {
  return props.variant === "space-between" && spacedBetween;
}, LI, pipedText, function (props) {
  return props.variant === "space-between" && {
    flexGrow: 1
  };
})));
Piped.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), {}, {
  variant: PropTypes.oneOf(["inline", "space-between"])
});
Piped.defaultProps = {
  variant: "inline"
};
export default {
  Piped: Piped,
  OL: OL,
  UL: UL,
  LI: LI,
  Inline: Inline
};